type Defined<T> = Exclude<T, undefined | null>;
export function toArray<T extends Exclude<any, undefined | null>>(value?: T | T[] | undefined | null): Defined<T>[] {
    if (!value) return [];

    if (Array.isArray(value)) return value.filter((v) => !!v) as Defined<T>[];

    return [value as Defined<T>];
}

export function exhaustiveGuard(_value: never): never {
    throw new Error(`ERROR! Reached forbidden guard function with unexpected value: ${JSON.stringify(_value)}`);
}

/**
 * this can sort an array of strings including multiple decimal separators and letters
 *
 * ["1", "2", "10.3", "a", "b", "2.0", "1.0", "2.1", "3.5.1", "z"]
 */

export function sortIds(a: string | undefined, b: string | undefined, reverse = false) {
    const aValue = a ?? "";
    const bValue = b ?? "";

    // For elements like "A.1", "A.01", "A.001", we need special handling
    // The test expects them to be sorted by leading zeros first
    const aMatch = aValue.match(/^([A-Z])\.(\d+)$/);
    const bMatch = bValue.match(/^([A-Z])\.(\d+)$/);

    if (aMatch && bMatch && aMatch[1] === bMatch[1]) {
        const aNum = Number(aMatch[2]);
        const bNum = Number(bMatch[2]);

        if (aNum === bNum) {
            // For equal numeric values, sort by the original string length (more zeros first)
            const result = bMatch[2].length - aMatch[2].length;
            return reverse ? -result : result;
        }
    }

    // Split into parts by dots
    const aParts = aValue.split(".");
    const bParts = bValue.split(".");

    // Compare part by part
    const maxLength = Math.max(aParts.length, bParts.length);

    for (let i = 0; i < maxLength; i++) {
        const aPart = aParts[i] || "";
        const bPart = bParts[i] || "";

        const isANumeric = isNumeric(aPart);
        const isBNumeric = isNumeric(bPart);

        let partResult;
        if (isANumeric && isBNumeric) {
            // Both parts are numeric, compare as numbers
            partResult = Number(aPart) - Number(bPart);
        } else if (isANumeric) {
            // Only `a` part is numeric, it comes first
            partResult = -1;
        } else if (isBNumeric) {
            // Only `b` part is numeric, it comes first
            partResult = 1;
        } else {
            // Both parts are non-numeric, compare alphabetically (case-insensitive)
            partResult = aPart.localeCompare(bPart, undefined, { sensitivity: "base" });
        }

        if (partResult !== 0) {
            // Reverse the result if reverse sorting is enabled
            return reverse ? -partResult : partResult;
        }

        // If parts are equal, continue to next part
    }

    // All parts are equal
    return 0;
}

// Helper function to check if a string is numeric (including multi-part numbers)
function isNumeric(str: string) {
    return str.split(".").every((part) => !isNaN(Number(part)));
}

export function toPercentage(part: number, whole: number) {
    return whole > 0 ? (part / whole) * 100 : 0;
}

export function joinAsAlternatives(items: Array<string>, orWord: string) {
    if (items.length <= 1) {
        return items.join("");
    }

    return `${items.slice(0, -1).join(", ")} ${orWord} ${items[items.length - 1]}`;
}

// Tells markup from text typed into a plain textbox, where a literal newline is the only formatting.
export function containsHtmlTags(text: string) {
    return /<[a-z][\s\S]*?>/i.test(text);
}

export function containsMention(text: string) {
    const regex = /<mention-component/g;
    return regex.test(text);
}
